import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { toast } from '../../toasts';
import paymentService from '../../services/payment.service';
import { getError, showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button } from '../../components/EntityList';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

/**
 * Rows are fetched once by the parent (DebtDetails) on page entry and passed
 * down here, rather than self-fetched on mount — this tab used to only fetch
 * when the user actually clicked into it, which showed a 0 count in the tab
 * label and a loading spinner every time the tab was revisited.
 */
function DebtPayments({ rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const { id } = useParams();
	const navigate = useNavigate();
	const [deletingPayment, setDeletingPayment] = useState(null);

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((payment) => {
			const key = payment.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (payment.amount || 0));
		});
		return [
			{
				label: 'Total cobrado',
				value: [...totalByCurrency.entries()].map(([currency, total]) => formatMoney(currency, total)).join(' · ') || '—',
				mono: true,
			},
			{ label: 'Cantidad de cobros', value: rows.length },
		];
	}, [rows]);

	function handleShowPaymentModal() {
		navigate(`/AdminScreen/debts/debt/${id}/newPayment`);
	}

	function handleEditPayment(payment) {
		navigate(`/AdminScreen/payments/editPayment/${payment._id}`, {
			state: { returnTo: `/AdminScreen/debts/debt/${id}/cobros` },
		});
	}

	function verComprobante(payment) {
		if (payment.comprobante) {
			window.open(payment.comprobante.fileLink, '_blank', 'noopener,noreferrer');
		}
	}

	async function handleConfirmDelete() {
		try {
			await paymentService.delete(deletingPayment._id);
			toast.success(t('msg.paymentDeleted'));
			onRefetch?.();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingPayment(null);
		}
	}

	const columns = [
		{ key: 'id', header: 'ID', render: (payment) => <IdChip>{payment.customId}</IdChip> },
		{ key: 'due', header: t('entity.due'), render: (payment) => payment.debtDue?.customId },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (payment) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(payment.currency, payment.amount)}</span>,
		},
		{ key: 'date', header: t('field.date'), render: (payment) => formatDate(payment.date) },
		{ key: 'folder', header: t('field.folder'), render: (payment) => payment.folder },
		{ key: 'status', header: t('field.status'), render: (payment) => payment.status },
	];

	const renderRowActions = (payment) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => handleEditPayment(payment) },
				payment.comprobante && { label: 'Ver comprobante', onClick: () => verComprobante(payment) },
				{ label: t('action.delete'), onClick: () => setDeletingPayment(payment), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end'>
				<Button size='sm' variant='primary' onClick={handleShowPaymentModal}>
					{t('title.newPayment')}
				</Button>
			</div>
			<Section title={t('title.collections')}>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={handleEditPayment}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron cobros para esta deuda.'
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingPayment)}
				handleClose={() => setDeletingPayment(null)}
				handleConfirm={handleConfirmDelete}
				title='Eliminar Cobro'
				description={t('msg.confirmDeletePayment')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default DebtPayments;
